import React from "react";
import {
    Dialog,
    DialogContent,
    Typography,
    IconButton,
    TextField,
    Alert,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import moment from "moment";
import { StyledButtonMain } from "../../common/Buttons";

/**
 * A firm needs validation when it has never been validated, or when its last
 * validation is older than 6 months. Shared by the case header button, the case
 * auto-open logic and the notice inside this modal.
 */
export const needsValidation = (firm: any): boolean => {
    if (!firm) return false;
    const lastValidated = firm["last_validated_at"];
    if (!lastValidated) return true;
    return moment(lastValidated).isBefore(moment().subtract(6, "months"));
};

interface DataValidationDrawerProps {
    open: boolean;
    onClose: () => void;
    firm: any;
    onVerify: (data: any) => void;
    onEmailClick?: () => void;
    children?: React.ReactNode;
}

const DataValidationDrawer: React.FC<DataValidationDrawerProps> = ({
    open,
    onClose,
    firm,
    onVerify,
    onEmailClick,
    children,
}) => {
    const [formData, setFormData] = React.useState<any>({
        _id: "",
        Employees: "",
        "Annual Revenue": "",
        "Employees 2025": "",
        "Annual Revenue 2025": "",
        "Employees 2024": "",
        "Annual Revenue 2024": "",
    });

    React.useEffect(() => {
        if (firm && open) {
            setFormData({
                _id: firm?._id || "",
                Employees: firm?.Employees || "",
                "Annual Revenue": firm?.["Annual Revenue"] || "",
                "Employees 2025": firm?.["Employees 2025"] || "",
                "Annual Revenue 2025": firm?.["Annual Revenue 2025"] || "",
                "Employees 2024": firm?.["Employees 2024"] || "",
                "Annual Revenue 2024": firm?.["Annual Revenue 2024"] || "",
            });
        }
    }, [firm, open]);

    const handleInputChange = (field: string, value: string) => {
        setFormData((prev: any) => ({
            ...prev,
            [field]: value,
        }));
    };

    const handleVerify = () => {
        onVerify(formData);
    };

    const showValidationNotice = needsValidation(firm);

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth={false}
            aria-labelledby="data-validation-dialog-title"
            PaperProps={{
                sx: {
                    width: "min(480px, calc(100% - 32px))",
                    maxWidth: "100%",
                    borderRadius: "10px",
                },
            }}
        >
            <div className="flex justify-between items-center px-6 pt-5 pb-2">
                <Typography variant="h5" id="data-validation-dialog-title">
                    Data Validation
                </Typography>
                <IconButton onClick={onClose} aria-label="close">
                    <CloseIcon />
                </IconButton>
            </div>

            <DialogContent dividers sx={{ px: 3, py: 2 }}>
                {firm && (
                    <div className="space-y-4">
                        {showValidationNotice && (
                            <Alert severity="warning">
                                This client's business data has not been validated in
                                the last 6 months. Please confirm the figures below.
                            </Alert>
                        )}

                        <div>
                            <Typography variant="subtitle2" color="textSecondary">
                                Company Name
                            </Typography>
                            <Typography variant="body1">
                                {firm?.["Company Name"]}
                            </Typography>
                        </div>

                        <div>
                            <Typography variant="subtitle2" color="textSecondary">
                                City
                            </Typography>
                            <Typography variant="body1">{firm?.City || "-"}</Typography>
                        </div>

                        <div>
                            <Typography variant="subtitle2" color="textSecondary">
                                Email
                            </Typography>
                            <Typography
                                variant="body1"
                                className="cursor-pointer hover:text-blue-600 hover:underline"
                                onClick={() => {
                                    if (firm?.Email && onEmailClick) {
                                        onEmailClick();
                                    }
                                }}
                            >
                                {firm?.Email || "-"}
                            </Typography>
                        </div>
                        <div>
                            <Typography variant="subtitle2" color="textSecondary">
                                Phone
                            </Typography>
                            <Typography variant="body1">{firm?.Phone || "-"}</Typography>
                        </div>

                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <TextField
                                    label="Employees"
                                    variant="outlined"
                                    size="small"
                                    type="number"
                                    fullWidth
                                    value={formData.Employees}
                                    onChange={(e) =>
                                        handleInputChange("Employees", e.target.value)
                                    }
                                />
                            </div>
                            <div>
                                <TextField
                                    label="Annual Revenue"
                                    variant="outlined"
                                    size="small"
                                    type="number"
                                    fullWidth
                                    value={formData["Annual Revenue"]}
                                    onChange={(e) =>
                                        handleInputChange("Annual Revenue", e.target.value)
                                    }
                                />
                            </div>
                        </div>

                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <TextField
                                    label="2025 Employees"
                                    variant="outlined"
                                    size="small"
                                    type="number"
                                    fullWidth
                                    value={formData["Employees 2025"]}
                                    onChange={(e) =>
                                        handleInputChange("Employees 2025", e.target.value)
                                    }
                                />
                            </div>
                            <div>
                                <TextField
                                    label="2025 Revenue"
                                    variant="outlined"
                                    size="small"
                                    type="number"
                                    fullWidth
                                    value={formData["Annual Revenue 2025"]}
                                    onChange={(e) =>
                                        handleInputChange("Annual Revenue 2025", e.target.value)
                                    }
                                />
                            </div>
                        </div>

                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <TextField
                                    label="2024 Employees"
                                    variant="outlined"
                                    size="small"
                                    type="number"
                                    fullWidth
                                    value={formData["Employees 2024"]}
                                    onChange={(e) =>
                                        handleInputChange("Employees 2024", e.target.value)
                                    }
                                />
                            </div>
                            <div>
                                <TextField
                                    label="2024 Revenue"
                                    variant="outlined"
                                    size="small"
                                    type="number"
                                    fullWidth
                                    value={formData["Annual Revenue 2024"]}
                                    onChange={(e) =>
                                        handleInputChange("Annual Revenue 2024", e.target.value)
                                    }
                                />
                            </div>
                        </div>

                        <div>
                            <Typography variant="subtitle2" color="textSecondary">
                                Last Validated
                            </Typography>
                            <Typography variant="body1">
                                {firm?.["last_validated_at"]
                                    ? new Date(firm["last_validated_at"]).toLocaleDateString()
                                    : "Never"}
                            </Typography>
                        </div>

                        <div className="pt-4">
                            <StyledButtonMain
                                variant="contained"
                                color="primary"
                                fullWidth
                                onClick={handleVerify}
                                size="large"
                            >
                                Verify
                            </StyledButtonMain>
                        </div>
                    </div>
                )}
            </DialogContent>
            {children}
        </Dialog>
    );
};

export default DataValidationDrawer;
